import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  Button,
  FullScreenLoading,
  StaticDropdown,
  TextInput,
} from "src/Components";
import {
  ICCOrderCriterionTypes,
  IUpdateCriterionDTO,
} from "src/Interfaces/ccOrder";
import { StepOrderSide } from "src/Interfaces/stepInterfaces";
import {
  getCCOrderCriterionTypes,
  updateCriterion,
} from "src/Requests/ccOrder";
import {
  fallbackCCOrderCriterionTypes,
  getCCOrderCriterionOptions,
  getCCOrderCriterionTypeName,
  getDefaultCCOrderCriterionType,
} from "src/Utils/ccOrderCriterionTypes";
import { packetErrorHandler } from "src/Utils/requestUtils";

interface Props {
  onClose: () => void;
  initialValues: IUpdateCriterionDTO;
  instrumentNames: string[];
  orderTypeStr: string;
  riskProfileId?: string;
}
const UpdateCriterion: React.FC<Props> = ({
  onClose,
  initialValues,
  instrumentNames,
  orderTypeStr,
  riskProfileId,
}) => {
  const [loading, setLoading] = useState(false);
  const [formValues, setFormValues] =
    useState<IUpdateCriterionDTO>(initialValues);
  const [criterionTypes, setCriterionTypes] =
    useState<ICCOrderCriterionTypes>(fallbackCCOrderCriterionTypes);

  useEffect(() => {
    getCCOrderCriterionTypes()
      .then(setCriterionTypes)
      .catch(packetErrorHandler);
  }, []);

  const criterionOptions = useMemo(
    () => getCCOrderCriterionOptions(criterionTypes, formValues?.orderType),
    [criterionTypes, formValues?.orderType],
  );

  useEffect(() => {
    if (!formValues?.orderType) return;

    const isAllowedType = criterionOptions.some(
      (item) => item.id?.toString() === formValues?.criterionType?.toString(),
    );
    if (isAllowedType) return;

    const defaultCriterionType = getDefaultCCOrderCriterionType(
      criterionTypes,
      formValues.orderType,
    );
    if (!defaultCriterionType) return;

    setFormValues((prev) => ({
      ...prev,
      criterionType: defaultCriterionType,
    }));
  }, [
    criterionOptions,
    criterionTypes,
    formValues?.orderType,
    formValues?.criterionType,
  ]);

  const changeHandler = (name: string, value: any) => {
    setFormValues({ ...formValues, [name]: value });
  };

  const onSendClick = () => {
    setLoading(true);
    updateCriterion(
      {
        ...formValues,
        newCriterion: formValues.newCriterion
          ? Number(formValues.newCriterion)
          : (null as any),
        criterionType: formValues.criterionType
          ? Number(formValues.criterionType)
          : null,
      },
      riskProfileId,
    )
      .then((resp) => {
        toast.success(resp.msg);
        onClose();
      })
      .catch(packetErrorHandler)
      .finally(() => setLoading(false));
  };

  const checkIsBtnDisabled = () => {
    if (!formValues.newCriterion) return true;
    return false;
  };

  return (
    <div className="flex w-[620px] max-w-full flex-col justify-start items-stretch gap-3 whitespace-nowrap text-xs border-2 border-gray-300 rounded py-3 px-3 mt-6">
      {loading ? <FullScreenLoading /> : <></>}

      <div className="grid grid-cols-2 gap-x-14 gap-y-3 font-bold">
        <div className="flex flex-row justify-start items-center gap-2">
          <div>فرایند:</div>
          <div>{orderTypeStr}</div>
        </div>
        <div />
        <div className="flex flex-row justify-start items-center gap-2">
          <div>نماد اول:</div>
          <div
            className={`${
              formValues.coveredCallOrderLegs?.[0]?.orderSide ===
              StepOrderSide.Buy
                ? "text-green-700"
                : "text-red-700"
            }`}
          >
            {instrumentNames?.[0]}
          </div>
        </div>
        <div className="flex flex-row justify-start items-center gap-2">
          <div>جهت اول:</div>
          <div
            className={`${
              formValues.coveredCallOrderLegs?.[0]?.orderSide ===
              StepOrderSide.Buy
                ? "text-green-700"
                : "text-red-700"
            }`}
          >
            {formValues.coveredCallOrderLegs?.[0]?.orderSide ===
            StepOrderSide.Buy
              ? "خرید"
              : "فروش"}
          </div>
        </div>

        <div className="flex flex-row justify-start items-center gap-2">
          <div>نماد دوم:</div>
          <div
            className={`${
              (formValues.coveredCallPositionStrategyEnum !== 8
                ? formValues.coveredCallOrderLegs?.[1]?.orderSide
                : formValues.coveredCallOrderSellLeg.orderSide) ===
              StepOrderSide.Buy
                ? "text-green-700"
                : "text-red-700"
            }`}
          >
            {instrumentNames?.[1]}
          </div>
        </div>
        <div className="flex flex-row justify-start items-center gap-2">
          <div>جهت دوم:</div>
          <div
            className={`${
              (formValues.coveredCallPositionStrategyEnum !== 8
                ? formValues.coveredCallOrderLegs?.[1]?.orderSide
                : formValues.coveredCallOrderSellLeg.orderSide) ===
              StepOrderSide.Buy
                ? "text-green-700"
                : "text-red-700"
            }`}
          >
            {(formValues.coveredCallPositionStrategyEnum !== 8
              ? formValues.coveredCallOrderLegs?.[1]?.orderSide
              : formValues.coveredCallOrderSellLeg.orderSide) ===
            StepOrderSide.Buy
              ? "خرید"
              : "فروش"}
          </div>
        </div>
        {formValues.coveredCallPositionStrategyEnum === 8 ? (
          <>
            <div className="flex flex-row justify-start items-center gap-2">
              <div>نماد سوم:</div>
              <div
                className={`${
                  formValues.coveredCallOrderSellLeg?.orderSide ===
                  StepOrderSide.Buy
                    ? "text-green-700"
                    : "text-red-700"
                }`}
              >
                {instrumentNames?.[2]}
              </div>
            </div>
            <div className="flex flex-row justify-start items-center gap-2">
              <div>جهت سوم:</div>
              <div
                className={`${
                  formValues.coveredCallOrderSellLeg?.orderSide ===
                  StepOrderSide.Buy
                    ? "text-green-700"
                    : "text-red-700"
                }`}
              >
                {formValues.coveredCallOrderSellLeg?.orderSide ===
                StepOrderSide.Buy
                  ? "خرید"
                  : "فروش"}
              </div>
            </div>
          </>
        ) : (
          <></>
        )}
        <div className="col-span-2 grid grid-cols-2 gap-4 items-end">
          {criterionOptions.length > 1 ? (
            <StaticDropdown
              dropdown={{
                name: "criterionType",
                placeholder: "معیار",
                extraLabel: true,
              }}
              options={criterionOptions}
              value={formValues.criterionType}
              onChange={changeHandler}
            />
          ) : (
            <div />
          )}
          <TextInput
            label={`معیار: ${getCCOrderCriterionTypeName(criterionTypes, formValues?.orderType, formValues.criterionType)}`}
            name="newCriterion"
            value={formValues.newCriterion?.toString()}
            onChange={changeHandler}
            classname="ltr !text-right"
          />
        </div>
      </div>

      <div className="flex flex-row justify-between items-center w-full">
        <Button
          onClick={onClose}
          text="لغو"
          variant="outlined"
          className="!w-16 !h-6 !text-xs"
        />
        <Button
          onClick={onSendClick}
          text="ارسال"
          variant="contained"
          className="!w-16 !h-6 !text-xs"
          disabled={checkIsBtnDisabled()}
        />
      </div>
    </div>
  );
};

export default UpdateCriterion;
